/**
 * SERVER STATE LIVES IN TANSTACK QUERY, AND NOWHERE ELSE.
 *
 * - A 4xx is an answer, not a flake: it is never retried. A network failure,
 *   a timeout, a 5xx or a 429 is retried twice.
 * - A mutation is never retried automatically. A write is retried by the
 *   person, with the same idempotency key, on a screen that says what happened.
 * - A 401 from any query means the session has ended: the handler registered by
 *   the app clears the session and sends the reader to sign in.
 */
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isAppError, isRetryable } from "@/api/problems";

let onUnauthenticated: (() => void) | undefined;

/** The app registers what "your session has ended" does. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

function reportUnauthenticated(error: unknown) {
  if (isAppError(error) && error.kind === "unauthenticated") onUnauthenticated?.();
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: reportUnauthenticated }),
    mutationCache: new MutationCache({ onError: reportUnauthenticated }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
